import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxHighlightText from "ds/components/ComboBox/HighlightText";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import ListBoxSection from "ds/components/ListBox/Group";
import ListBoxItem from "ds/components/ListBox/Item";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { useDebounce } from "hooks/useDebounce";
import useTypedContext from "hooks/useTypedContext";
import { SearchModulesOutput, SearchStacksOutput } from "types/generated";
import { SpacesContext } from "views/Account/SpacesProvider";
import Card from "ds/components/Card";

import { AttachedProject } from "../AttachedProject/types";
import NewContextAutoAttachedProjects from "../AutoAttachedProjects";
import { ContextCreationWizardStepName } from "../constants";
import { ContextFormContext } from "../context";
import NewContextFooter from "../Footer";
import { useAutoAttachmentLabels } from "../hooks/useAutoAttachmentLabels";
import { useSaveContext } from "../hooks/useSaveContext";
import NewContextManuallyAttachedProjects from "../ManuallyAttachedProjects";
import { ContextFormFields } from "../types";
import { SEARCH_STACKS_AND_MODULES } from "./gql";
import NewContextAttachContextProjectComboBoxItemBadge from "./ProjectComboBoxItemBadge";
import { AttachableProject, AttachableProjectType } from "./types";

const NewAttachContext = () => {
  const { spaceHierarchy } = useTypedContext(SpacesContext);

  const [project, setProject] = useState<string | null>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [autoAttachedProjectCount, setAutoAttachedProjectCount] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const { handleSubmit, getValues, setValue, watch } = useFormContext<ContextFormFields>();
  const { setAttachedProjects } = useTypedContext(ContextFormContext);

  const space = getValues("space");
  const attachedProjectIds = watch("attachedProjectIds");

  useObserveForWarning(
    !!project,
    <>
      You have not attached the <strong>selected project</strong>. Do you want to continue without
      attaching it?
    </>
  );

  // TODO: Remove this once backend returns space-hierarchy
  const spacePath = spaceHierarchy[space];

  const { data: searchData, loading: searching } = useQuery<{
    searchStacks: SearchStacksOutput;
    searchModules: SearchModulesOutput;
  }>(SEARCH_STACKS_AND_MODULES, {
    variables: {
      input: {
        first: 50,
        fullTextSearch: debouncedSearchQuery,
        predicates: [
          {
            constraint: {
              hierarchyNodeValueEquals: [spacePath],
            },
            field: "space",
          },
        ],
      },
    },
    skip: !spacePath,
  });

  const [attachableProjects, attachedProjects] = useMemo(() => {
    const searchResults = [
      ...(searchData?.searchStacks?.edges || []),
      ...(searchData?.searchModules?.edges || []),
    ];

    const attachableProjectResults: { label: string; items: AttachableProject[] }[] = [
      { label: AttachableProjectType.STACK, items: [] },
      { label: AttachableProjectType.MODULE, items: [] },
    ];
    const attachedProjectResults: AttachedProject[] = [];

    for (const edge of searchResults) {
      const { id, name, spaceDetails, __typename } = edge.node;
      const isModule = __typename === "Module";

      if (attachedProjectIds.includes(edge.node.id)) {
        attachedProjectResults.push({
          id,
          name,
          spaceDetails,
          isModule,
        });
      } else {
        const project = {
          value: id,
          label: name,
          spaceDetails,
          type: isModule ? AttachableProjectType.MODULE : AttachableProjectType.STACK,
        };

        if (isModule) {
          attachableProjectResults[1].items.push(project);
        } else {
          attachableProjectResults[0].items.push(project);
        }
      }
    }

    return [
      attachableProjectResults.filter((section) => section.items.length > 0),
      attachedProjectResults,
    ];
  }, [searchData, attachedProjectIds]);

  useEffect(() => {
    setAttachedProjects(attachedProjects);
  }, [attachedProjects, setAttachedProjects]);

  const labelsValue = useAutoAttachmentLabels(getValues("labels"));

  const handleAttach = () => {
    if (!project) {
      return;
    }
    setValue("attachedProjectIds", attachedProjectIds.concat(project));
    setProject("");
  };

  const handleDetach = (id: string) => {
    setValue(
      "attachedProjectIds",
      attachedProjectIds.filter((projectId) => projectId !== id)
    );
  };

  const [saveContext, isSavingContext] = useSaveContext(ContextCreationWizardStepName.Attachment);

  const analyticsData = {
    location: ContextCreationWizardStepName.Attachment,
    "manually-attached": attachedProjectIds.length,
    "auto-attached": autoAttachedProjectCount,
    attached: attachedProjectIds.length > 0 || autoAttachedProjectCount > 0,
  };

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Attach context (optional)"
          description="If you can’t see the stack or module that you wanted the context to attach to on the Auto-attached list you can add it manually here."
        />

        <Card variant="secondary" titleTag="h3" title="Attach to projects" contentGap="large">
          <ComboBox
            label="Project"
            tooltipInfo={
              <>
                <TooltipModalTitle>Project</TooltipModalTitle>
                <TooltipModalBody align="start">
                  <Typography tag="p" variant="p-body3">
                    Projects are all your stacks and modules.
                  </Typography>
                </TooltipModalBody>
              </>
            }
            isLoading={searching}
            items={attachableProjects}
            value={project}
            onChange={setProject}
            onInputChange={setSearchQuery}
          >
            {(item) => (
              <ListBoxSection id={item.label} label={item.label} items={item.items}>
                {(item) => (
                  <ListBoxItem id={item.value} textValue={item.label}>
                    <ComboBoxHighlightText text={item.label} />
                    <NewContextAttachContextProjectComboBoxItemBadge {...item} />
                  </ListBoxItem>
                )}
              </ListBoxSection>
            )}
          </ComboBox>
          <Box align="end" direction="column" fullWidth>
            <Button
              variant="contrast"
              onPress={handleAttach}
              disabled={attachableProjects.length === 0 || !project}
            >
              Attach
            </Button>
          </Box>
        </Card>

        <Box gap="x-large" margin="x-large 0 0 0" direction="column">
          <NewContextManuallyAttachedProjects items={attachedProjects} onDetach={handleDetach} />
          <NewContextAutoAttachedProjects
            space={space}
            labels={labelsValue}
            setAutoAttachedProjectCount={setAutoAttachedProjectCount}
            setAutoAttachProject={setAttachedProjects}
          />
        </Box>
      </FullScreenModalBody>

      <NewContextFooter
        isCreatingContext={isSavingContext}
        onCreateContextClick={handleSubmit(saveContext)}
        continueClickAnalyticsProps={analyticsData}
      />
    </>
  );
};

export default NewAttachContext;
